import { SystemEntity } from '@erp/shared-types';
import { Badge } from '@/components/ui/badge';
import {
  HoverCard,
  HoverCardTrigger,
  HoverCardContent,
} from '@/components/ui/hover-card';
import { cn } from '@/lib/utils';
import { TruckIcon } from '@phosphor-icons/react';
import { SystemEntityPreview } from '../preview-ui';
import { useShipment } from '@/api/rpc/hooks/supply-chain/useShipments';
import { T, useGT } from 'gt-react';

interface ShipmentInlineProps {
  entityType: SystemEntity; // Required by inline component registry but not used - type is always SCM_SHIPMENT
  entityId: string;
  className?: string;
}

export function ShipmentInline({ entityId, className }: ShipmentInlineProps) {
  const gt = useGT();
  const { data: shipmentData, isLoading } = useShipment(entityId);

  // Prefer originLoadNumber, fallback to originLoadId, then to truncated UUID
  const shipmentNumber = isLoading
    ? gt('Loading...', { context: 'loading shipment inline badge' })
    : shipmentData?.shipment?.originLoadNumber ||
      shipmentData?.shipment?.originLoadId ||
      shipmentData?.shipment?.id.slice(0, 8) ||
      gt('Unknown', { context: 'unknown shipment identifier' });

  return (
    <HoverCard openDelay={200} closeDelay={200}>
      <HoverCardTrigger asChild>
        <div className="inline-block">
          <Badge
            variant="outline"
            className={cn('gap-1.5 cursor-pointer', className)}
          >
            <TruckIcon className="size-3" />
            <span>
              <span className="text-muted-foreground font-mono">
                <T context="system entity type label">Shipment</T>
              </span>{' '}
              {shipmentNumber}
            </span>
          </Badge>
        </div>
      </HoverCardTrigger>
      <HoverCardContent
        className="w-auto p-0 pointer-events-auto"
        align="start"
        side="bottom"
        sideOffset={2}
        onPointerEnter={(e) => e.stopPropagation()}
        onPointerLeave={(e) => e.stopPropagation()}
      >
        <SystemEntityPreview
          entityType={SystemEntity.SCM_SHIPMENT}
          entityId={entityId}
        />
      </HoverCardContent>
    </HoverCard>
  );
}
